export interface User {
  id: string
  mnemonic: string
  created_at: string
}

export interface GameSubmission {
  userId: string
  wordSubmitted: string
  attemptsCount: number
  attemptWords: string[]
}

export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

class ApiService {
  private baseUrl = '/api'

  async createUser(): Promise<User> {
    const response = await fetch(`${this.baseUrl}/user/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Check if we got HTML instead of JSON (likely 404)
    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('API server not available')
    }

    const result = await response.json()
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to create user')
    }

    return result.user
  }

  async getUser(userId: string): Promise<User | null> {
    try {
      const response = await fetch(`${this.baseUrl}/user/${userId}`)
      
      // Check if we got HTML instead of JSON (likely 404)
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('API server not available')
      }
      
      const result = await response.json()
      
      if (!result.success) {
        if (response.status === 404) {
          return null
        }
        throw new Error(result.error || 'Failed to fetch user')
      }

      return result.user
    } catch (error) {
      console.error('Error fetching user:', error)
      return null
    }
  }

  async submitGame(submission: GameSubmission): Promise<number> {
    const response = await fetch(`${this.baseUrl}/game/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: submission.userId,
        wordSubmitted: submission.wordSubmitted,
        attemptsCount: submission.attemptsCount,
        attemptWords: submission.attemptWords,
      }),
    })

    // Check if we got HTML instead of JSON (likely 404)
    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('API server not available')
    }

    const result = await response.json()
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to submit game')
    }

    return result.submissionId
  }

  async getUserHistory(userId: string): Promise<any[]> {
    const response = await fetch(`${this.baseUrl}/user/${userId}/history`)
    const result = await response.json()
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch user history')
    }

    return result.history
  }
}

export const apiService = new ApiService()